import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { VideoCallPage } from './pages/VideoCallPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { DashboardPage } from './pages/admin/DashboardPage';
import { ReportsPage } from './pages/admin/ReportsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { UserDetailsPage } from './pages/admin/UserDetailsPage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { PremiumPage } from './pages/PremiumPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { SettingsPage } from './pages/SettingsPage';
import { InterestSelectionPage } from './pages/InterestSelectionPage';
import { PremiumBadge } from './components/PremiumBadge';
import './App.css';

function HomePage() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
      <h1 style={{ color: 'var(--color-deep-purple)', marginBottom: '1rem' }}>
        VibeTalk (Sample Update)
      </h1>
      <p style={{ color: 'var(--color-gray-600)', fontSize: '1.2rem', marginBottom: '2rem' }}>
        Feel something real
      </p>
      <div style={{
        padding: '2rem',
        background: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {isAuthenticated ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <h2 style={{ color: 'var(--color-warm-orange)', margin: 0 }}>
                Welcome back, {user?.email}!
              </h2>
              {user?.isPremium && <PremiumBadge size="small" />}
            </div>
            <p style={{ color: 'var(--color-gray-700)', marginBottom: '1rem' }}>
              Ready to connect with someone new?
            </p>
            {!user?.isEmailVerified && (
              <div style={{
                background: '#fff3cd',
                color: '#856404',
                padding: '0.75rem',
                borderRadius: 'var(--radius-md)',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                Please verify your email to unlock all features
              </div>
            )}
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/video')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--color-warm-orange)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Start Chatting
              </button>
              {user?.isPremium ? (
                <button
                  onClick={() => navigate('/subscription')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--color-soft-blue)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Manage Subscription
                </button>
              ) : (
                <button
                  onClick={() => navigate('/premium')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ⭐ Upgrade to Premium
                </button>
              )}
              <button
                onClick={() => navigate('/settings')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--color-gray-300)',
                  color: 'var(--color-dark-navy)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Settings
              </button>
              <button
                onClick={logout}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--color-gray-300)',
                  color: 'var(--color-dark-navy)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Logout
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ color: 'var(--color-warm-orange)', marginBottom: '1rem' }}>
              Welcome to VibeTalk
            </h2>
            <p style={{ color: 'var(--color-gray-700)' }}>
              Random video chat with real connections.
              Meet new people from around the world.
            </p>
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <Link to="/signup" style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--color-warm-orange)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontWeight: '600',
                textDecoration: 'none'
              }}>
                Get Started
              </Link>
              <Link to="/login" style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--color-soft-blue)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontWeight: '600',
                textDecoration: 'none'
              }}>
                Log In
              </Link>
            </div>
          </>
        )}
        <div style={{ marginTop: '2rem', padding: '1rem', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)' }}>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', margin: 0 }}>
            🚀 Backend API: <code>{process.env.REACT_APP_API_URL || 'Not configured'}</code>
          </p>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/premium" element={<PremiumPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutPage />} />
          <Route path="/checkout/cancel" element={<CheckoutPage />} />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/interests"
            element={
              <ProtectedRoute>
                <InterestSelectionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/video"
            element={
              <ProtectedRoute>
                <VideoCallPage />
              </ProtectedRoute>
            }
          />
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:userId" element={<UserDetailsPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
